import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, isFirebaseConfigured, firebaseConfigError } from '../firebase';

const AuthContext = createContext();
const TOKEN_REFRESH_INTERVAL_MS = 55 * 60 * 1000; // refresh every 55 min, well before Firebase's 1hr expiry

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const refreshIntervalRef = useRef(null);

  const clearRefreshInterval = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (!isFirebaseConfigured || !auth) {
      if (!isFirebaseConfigured) {
        console.error(firebaseConfigError);
      }

      setUser(null);
      setToken(null);
      localStorage.removeItem('firebaseToken');
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearRefreshInterval();

      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken();
        setUser(firebaseUser);
        setToken(idToken);
        localStorage.setItem('firebaseToken', idToken);

        refreshIntervalRef.current = setInterval(async () => {
          try {
            const refreshedToken = await firebaseUser.getIdToken(true);
            setToken(refreshedToken);
            localStorage.setItem('firebaseToken', refreshedToken);
          } catch {
            // session ended externally, let onAuthStateChanged handle it
          }
        }, TOKEN_REFRESH_INTERVAL_MS);
      } else {
        setUser(null);
        setToken(null);
        localStorage.removeItem('firebaseToken');
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      clearRefreshInterval();
    };
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
